import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { getTutor } from "../lib/api";
import { Avatar } from "../components/Avatar";
import { relTime } from "../lib/format";
import { toast } from "sonner";

export default function TutorPage() {
  const { id } = useParams();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const navigate = useNavigate();

  const loadTutor = useCallback(async () => {
    try {
      setTutor(await getTutor(id));
    } catch (error) {
      setError("Tutor no encontrado o algo se ha caído.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTutor();
  }, [loadTutor]);

  const startCall = () => navigate("/reservar");

  if (loading) {
    return (
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16">
        <div className="wk-skel mb-6" style={{ height: 20, width: 200 }} />
        <div className="wk-skel" style={{ height: 60, width: "60%" }} />
        <div className="mt-10 grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="wk-skel" style={{ height: 320 }} />
          <div className="wk-skel" style={{ height: 320 }} />
        </div>
      </div>
    );
  }

  if (error || !tutor) {
    return (
    <div className="max-w-[720px] mx-auto px-6 py-24 text-center wk-card p-12" style={{ borderColor: "#7228C8", borderWidth: 2 }}>
        <h1 className="text-[26px] font-bold tracking-hero" style={{ color: "#1E1924" }}>Este tutor se ha esfumado.</h1>
        <p className="mt-3" style={{ color: "#70667A" }}>{error || "Puede que ya no esté activo."}</p>
        <Link to="/tutores" className="wk-btn wk-btn-primary mt-6 inline-flex" data-testid="tutor-back-btn">Volver al catálogo</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
      <Link to="/tutores" className="text-[13px] hover:underline" style={{ color: "#70667A" }} data-testid="tutor-breadcrumb">
        ← Catálogo
      </Link>

      <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
        <div>
          <div className="flex items-start gap-6">
            <Avatar initials={tutor.initials} color={tutor.avatar_color} size={88} ring />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[36px] lg:text-[44px] font-extrabold tracking-hero leading-[1.1]" style={{ color: "#1E1924" }} data-testid="tutor-name">
                  {tutor.display_name}
                </h1>
                <span className="text-[10px] px-2 py-1 rounded font-medium tracking-wide uppercase" style={{ background: "rgba(114,40,200,0.08)", color: "#7228C8" }}>
                  Identidad protegida
                </span>
              </div>
              <div className="mt-2 text-[15px]" style={{ color: "#70667A" }}>
                {tutor.country} · {tutor.accent} · <span className="font-mono-num">{tutor.rating.toFixed(2)}</span> ★
              </div>
              <div className="mt-1 text-[13px]" style={{ color: "#70667A" }}>
                {tutor.sessions_count.toLocaleString("es-ES")} clases · {Math.round(tutor.minutes_taught / 60)} h enseñando · dado de alta {relTime(tutor.created_at)}
              </div>
            </div>
          </div>

          <p className="mt-8 text-[17px] leading-[1.7] max-w-prose-tight" style={{ color: "#1E1924" }} data-testid="tutor-bio">
            {tutor.bio}
          </p>

          {/* Audio demo simulado */}
          <div className="mt-10 wk-card p-6">
            <div className="wk-eyebrow mb-3">Demo de 15 segundos</div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setPlaying((p) => !p);
                  if (!playing) setTimeout(() => setPlaying(false), 15000);
                }}
                className="wk-btn wk-btn-dark"
                style={{ padding: "12px 20px" }}
                data-testid="tutor-play-demo"
              >
                {playing ? "Pausar" : "Reproducir demo"}
              </button>
              <div className="flex-1 h-12 flex items-center gap-0.5">
                {Array.from({ length: 48 }, (_, index) => `audio-bar-${index}`).map((barId, index) => (
                  <div
                    key={barId}
                    style={{
                      flex: 1,
                      height: `${20 + Math.sin(index * 0.7) * 15 + Math.random() * 8}px`,
                      background: playing ? "#7228C8" : "#DEDAE2",
                      borderRadius: 2,
                      transition: "background 200ms",
                    }}
                  />
                ))}
              </div>
              <div className="font-mono-num text-[13px] tabular-nums" style={{ color: "#70667A" }}>
                00:15
              </div>
            </div>
            <p className="text-[12px] mt-3" style={{ color: "#70667A" }}>
              Audio demo simulado. En Fase 1 se conecta al almacenamiento y al reproductor real.
            </p>
          </div>

          {/* Ficha compacta */}
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Especialidad</div>
              <div className="mt-1.5 text-[15px] font-semibold" style={{ color: "#1E1924" }}>{tutor.specialty}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Acento</div>
              <div className="mt-1.5 text-[15px] font-semibold" style={{ color: "#1E1924" }}>{tutor.accent}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Pack</div>
              <div className="mt-1.5 text-[15px] font-mono-num font-semibold" style={{ color: "#1E1924" }}>$22</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em]" style={{ color: "#70667A" }}>Incluye</div>
              <div className="mt-1.5 text-[15px] font-semibold" style={{ color: "#1E1924" }}>2 sesiones de 30 min</div>
            </div>
          </div>

          <div className="mt-10 wk-card p-5" style={{ background: "rgba(30,25,36,0.03)" }}>
            <div className="text-[13px] font-semibold" style={{ color: "#1E1924" }}>Aviso importante</div>
            <p className="text-[12px] mt-1 leading-[1.55]" style={{ color: "#70667A" }}>
              La identidad completa del tutor no se muestra ni se comparte. Está prohibido intentar contactar con el tutor fuera de WeSpik o pedir sus datos personales. Vulnerar esto es motivo de baja inmediata.
            </p>
          </div>
        </div>

        <aside className="wk-card wk-card-elev p-7 lg:sticky lg:top-24">
          <div className="wk-eyebrow mb-3">Pack de prueba</div>
          <div className="flex items-baseline gap-2">
            <div className="font-mono-num text-[42px] font-extrabold tracking-hero leading-none" style={{ color: "#1E1924" }}>
              $22
            </div>
          </div>
          <div className="text-[13px] mt-2" style={{ color: "#70667A" }}>
            Dos sesiones de conversación de 30 min con informe de pronunciación tras cada una.
          </div>
          <div className="h-px my-5" style={{ background: "#DEDAE2" }} />
          <div className="text-[13px]" style={{ color: "#70667A" }}>
            Tutor elegido: <span className="font-semibold" style={{ color: "#1E1924" }}>{tutor.display_name}</span>
          </div>
          <button onClick={startCall} className="wk-btn wk-btn-primary w-full mt-5" data-testid="tutor-start-call-btn">
            Reservar mi primera sesión · $22
          </button>
          <button onClick={() => navigate("/tutores")} className="wk-btn wk-btn-ghost w-full mt-2.5" data-testid="tutor-topup-btn">
            Ver otros tutores
          </button>
          <p className="text-[12px] mt-4 leading-[1.5]" style={{ color: "#70667A" }}>
            La videollamada y el informe son demostraciones visuales del recorrido de aprendizaje.
          </p>
        </aside>
      </div>
    </div>
  );
}
